import { authService } from './auth.service';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export interface Project {
  id: string;
  name: string;
  description?: string;
  client_id: string;
  client_name?: string;
  project_lead_id?: string;
  project_lead_name?: string;
  address?: string;
  status: 'planning' | 'in_progress' | 'completed' | 'cancelled' | 'on_hold';
  budget: number;
  start_date?: number;
  end_date?: number;
  progress?: number;
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface CreateProjectData {
  name: string;
  description?: string;
  client_id: string;
  project_lead_id?: string;
  address?: string;
  budget: number;
  start_date?: number;
  end_date?: number;
  status?: Project['status'];
}

export interface UpdateProjectData {
  name?: string;
  description?: string;
  client_id?: string;
  project_lead_id?: string;
  address?: string;
  budget?: number;
  start_date?: number;
  end_date?: number;
  status?: Project['status'];
  progress?: number;
}

export interface ProjectStats {
  total: number;
  by_status: Record<string, number>;
  total_budget: number;
  average_progress: number;
}

export interface ProjectWithDetails extends Project {
  clientName?: string;
  projectLeadName?: string;
  advancePercentage?: number;
}

export interface UpdateProjectData extends Partial<CreateProjectData> {}

export const projectService = {
  // ============================================================
  // OBTENER PROYECTOS
  // ============================================================
  
  async getProjects(): Promise<Project[]> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects`);
    return response.data || [];
  },

  async getProject(id: string): Promise<Project> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects/${id}`);
    return response.data;
  },

  // ============================================================
  // CREAR/ACTUALIZAR/ELIMINAR PROYECTO
  // ============================================================
  
  async createProject(data: CreateProjectData): Promise<Project> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response.data;
  },

  async updateProject(id: string, data: UpdateProjectData): Promise<Project> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    return response.data;
  },

  async deleteProject(id: string): Promise<void> {
    await authService.fetchWithAuth(`${API_URL}/api/projects/${id}`, {
      method: 'DELETE'
    });
  },

  // ============================================================
  // ESTADÍSTICAS Y REPORTES
  // ============================================================
  
  async getProjectStats(): Promise<ProjectStats> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects/stats`);
    return response.data;
  },

  async getProjectTeam(projectId: string): Promise<any[]> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects/${projectId}/team`);
    return response.data || [];
  },

  async getProjectAdvances(projectId: string): Promise<any[]> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects/${projectId}/advances`);
    return response.data || [];
  },

  async updateProjectProgress(projectId: string, progress: number): Promise<Project> {
    const response = await authService.fetchWithAuth(`${API_URL}/api/projects/${projectId}/progress`, {
      method: 'PATCH',
      body: JSON.stringify({ progress })
    });
    return response.data;
  }
};